// Properties — Office records.
//
//   GET    /api/clients/properties/all            list (+ client_name, job_count)
//   GET    /api/clients/properties/:id/detail     one property + its jobs
//   GET    /api/clients                           for the client select
//   POST   /api/clients/properties                create   (PropertyForm)
//   PUT    /api/clients/properties/:id            edit     (PropertyForm)
//   DELETE /api/clients/properties/:id            delete   (clients.delete)

import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { del } from '../../shared/http';
import { useCan } from '../../../hooks/usePermission';
import { DataTable, Pill, FwButton, FwSelect, Skeleton, EmptyState } from '../../../ui/fw';
import {
  useOfficeQuery, useAction, PageHeader, Toolbar, SearchBox, Panel, Split, DetailList, DetailRow,
  ActivePill, LoadError, ConfirmSheet, matches, factFallback, fmtDateTime, hoursOf, jobTone,
  useRecordView, DetailActions, ACTIVE_VALUES, ACTIVE_LABELS, matchesActive, readFilter,
} from './OfficePage';
import PropertyForm from './PropertyForm';
import { crewSummary, statusLabel } from '../../shared/jobFields';
import { jobRecordHref } from '../../shared/jobLink';

/**
 * As a card: which place, whose it is, is it live, where it is.
 *
 * The CLIENT is the subtitle rather than the address, because property names
 * here are not unique or self-describing — "Front lawn", "Main house",
 * "Verge" — and the thing that tells two of them apart is whose they are. The
 * street then answers "which one", as the first fact.
 *
 * `city` is hidden: at 320px the fact grid is a single column, so a third
 * fact is a third ROW, and the town is the least load-bearing of the three
 * (Address and Jobs both change what you do next; the town is in the detail
 * panel's Address line, and search still matches on it either way).
 */
const COLUMNS = [
  { key: 'name', label: 'Property', role: 'title' },
  { key: 'client_name', label: 'Client', role: 'subtitle' },
  // Measured at 320px on 2026-09-11: "Back paddock" — a real shape, a paddock
  // behind a house that has no street number of its own — rendered
  // `Address —` as one of its two facts. Half the card's information was a
  // dash. The town is the fallback because it is the next thing that says
  // WHERE, and on a card it is otherwise invisible: `city` is role 'hidden'
  // (see the note above), so this duplicates nothing that is already on screen.
  { key: 'address', label: 'Address', role: 'fact', render: (p) => factFallback([p.address, [p.city, p.state].filter(Boolean).join(', ')], 'Not recorded') },
  { key: 'city', label: 'City', role: 'hidden', render: (p) => [p.city, p.state].filter(Boolean).join(', ') || '—' },
  { key: 'job_count', label: 'Jobs', align: 'right', role: 'fact', render: (p) => p.job_count ?? 0 },
  { key: 'is_active', label: 'Status', role: 'badge', render: (p) => <ActivePill active={p.is_active} /> },
];

// Jobs at one property: what, who leads it, how it is going.
const JOB_COLUMNS = [
  { key: 'title', label: 'Job', role: 'title' },
  // "To do", not "todo": the same words the Jobs list uses (jobFields.js).
  { key: 'status', label: 'Status', role: 'badge', render: (j) => <Pill tone={jobTone(j.status)}>{statusLabel(j.status)}</Pill> },
  // "Unassigned", said plainly — not a dash. This is the owner's complaint in
  // as many words: "Need to see who's assigned/unassigned". Through
  // `crewSummary`, which is the app's ONE rendering of who is on a job
  // (apps/shared/jobFields.js) and is what the Office jobs list and the FIELD
  // job card both already print; it falls back to the `user_name` lead pointer
  // when the payload carries no assignee roster, which is this endpoint's
  // shape, and to 'Unassigned' when there is no lead either.
  { key: 'user_name', label: 'Lead', role: 'subtitle', render: crewSummary },
  { key: 'entry_count', label: 'Entries', align: 'right', role: 'fact', render: (j) => j.entry_count ?? 0 },
  { key: 'updated_at', label: 'Updated', role: 'fact', render: (j) => fmtDateTime(j.updated_at) },
];

function PropertyDetail({ id, canUpdate, canDelete, full, onToggleFull, onEdit, onClose, onDeleted }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // A job here opens its record — classic linked each one; the rebuilt table
  // was a list you could read and not act on. Only with job oversight: the
  // Jobs screen finds the record in GET /api/jobs, which answers a role
  // without it (a bookkeeper) with its own and unassigned jobs only, so the
  // job they tapped would usually not be there (second review of #738; the
  // same rule as Overview's Jobs tiles).
  const allJobs = useCan('jobs', 'manage_all');
  const teamJobs = useCan('jobs', 'manage_team');
  const openJob = allJobs || teamJobs
    ? (j) => { const href = jobRecordHref(j && j.id); if (href) navigate(href); }
    : undefined;
  const detailQ = useOfficeQuery(['property', id], `/clients/properties/${id}/detail`);
  const [confirm, setConfirm] = useState(false);
  const { busy, error, run } = useAction();
  const p = detailQ.data;

  const remove = async () => {
    const ok = await run(async () => {
      await del(`/clients/properties/${id}`);
      await qc.invalidateQueries({ queryKey: ['properties'] });
      await qc.invalidateQueries({ queryKey: ['clients'] });
    }, 'Could not delete the property.');
    if (ok) { setConfirm(false); onDeleted(); }
  };

  return (
    <Panel
      title={p ? p.name : 'Property'}
      actions={<DetailActions full={full} onToggleFull={onToggleFull} onClose={onClose} />}
    >
      {detailQ.isPending ? <Skeleton lines={5} /> : null}
      <LoadError error={detailQ.error} fallback="Could not load this property." />
      <LoadError error={error ? { message: error } : null} />
      {p ? (
        <>
          <DetailList>
            <DetailRow label="Client">{p.client_name}</DetailRow>
            <DetailRow label="Status"><ActivePill active={p.is_active} /></DetailRow>
            <DetailRow label="Address">{[p.address, p.city, p.state, p.zip_code].filter(Boolean).join(', ') || '—'}</DetailRow>
            {p.notes ? <DetailRow label="Notes">{p.notes}</DetailRow> : null}
          </DetailList>
          {canUpdate || canDelete ? (
            <div className="or-actions">
              {canUpdate ? <FwButton variant="ghost" icon="edit" onClick={() => onEdit(p)}>Edit</FwButton> : null}
              {canDelete ? <FwButton variant="stop" icon="trash" onClick={() => setConfirm(true)}>Delete</FwButton> : null}
            </div>
          ) : null}
          <Panel className="or-panel--flush" title={`Jobs (${(p.jobs || []).length})`}>
            <DataTable dense columns={JOB_COLUMNS} rows={p.jobs || []} onRowClick={openJob} empty="No jobs at this property yet." caption={`Jobs at ${p.name}`} />
          </Panel>
          {(p.jobs || []).length > 0 ? (
            <p className="or-muted">
              {hoursOf((p.jobs || []).reduce((s, j) => s + (Number(j.total_time) || 0), 0))} hours tracked here in total.
            </p>
          ) : null}
        </>
      ) : null}
      <ConfirmSheet
        open={confirm}
        title={`Delete ${p ? p.name : 'this property'}?`}
        confirmLabel="Delete property"
        busy={busy}
        onConfirm={remove}
        onClose={() => setConfirm(false)}
      >
        <p>Jobs at this property keep their history but lose the property link. If crews might return, deactivate it instead.</p>
      </ConfirmSheet>
    </Panel>
  );
}

export default function Properties() {
  const canCreate = useCan('clients', 'create');
  const canUpdate = useCan('clients', 'update');
  const canDelete = useCan('clients', 'delete');
  const propsQ = useOfficeQuery(['properties'], '/clients/properties/all');
  const clientsQ = useOfficeQuery(['clients'], '/clients');

  const [q, setQ] = useState('');
  // The open property and its width live in the URL — OfficePage.js
  // useRecordView. This detail carries a jobs table of its own, which is the
  // one that most wants the room.
  const view = useRecordView();
  const [sheet, setSheet] = useState(null); // { property } | null

  // The Active filter in the URL — see OfficePage.js ACTIVE_VALUES for why it
  // is answered here rather than by sending `active_only` to a query key the
  // job form's property picker shares.
  const [params, setParams] = useSearchParams();
  const active = readFilter(params, 'active', ACTIVE_VALUES);
  const setActive = (value) => {
    const next = new URLSearchParams(params);
    if (value) next.set('active', value); else next.delete('active');
    setParams(next, { replace: true });
  };

  const properties = useMemo(() => propsQ.data || [], [propsQ.data]);
  const rows = useMemo(
    () => properties.filter((p) => matchesActive(p, active) && matches(p, ['name', 'client_name', 'address', 'city'], q)),
    [properties, q, active],
  );
  const filtersOn = !!(q || active);
  const clearFilters = () => { setActive(''); setQ(''); };
  const clientOptions = useMemo(() => (clientsQ.data || []).map((c) => ({ id: c.id, name: c.name })), [clientsQ.data]);

  const table = propsQ.isPending ? <Skeleton lines={6} /> : propsQ.error ? (
    <LoadError error={propsQ.error} fallback="Could not load properties." />
  ) : properties.length === 0 ? (
    <EmptyState icon="mapPin" title="No properties yet" action={canCreate ? <FwButton icon="plus" onClick={() => setSheet({ property: null })}>New property</FwButton> : null}>
      A property is a place a crew goes. Each belongs to a client.
    </EmptyState>
  ) : (
    <Panel className="or-panel--flush">
      <DataTable
        columns={COLUMNS}
        rows={rows}
        onRowClick={(p) => view.open(p.id)}
        empty={filtersOn ? 'Nothing matches the search or the filter above.' : 'No properties.'}
        caption="Properties"
      />
    </Panel>
  );

  return (
    <div className="or-page" data-screen="office-Properties">
      {/* `N of M`, as Jobs, Crew and Clients do. A bare "4 properties" stayed
          put over a list the search had narrowed to one. */}
      <PageHeader title="Properties" meta={propsQ.data ? `${rows.length} of ${properties.length}` : null}>
        {canCreate ? <FwButton icon="plus" onClick={() => setSheet({ property: null })}>New property</FwButton> : null}
      </PageHeader>
      {/* A search box over a list that is not on the page is a control that
          appears to do nothing. In full screen the record is the page. */}
      {view.full ? null : (
        <Toolbar>
          <SearchBox value={q} onChange={setQ} placeholder="Search properties" label="Search properties" />
          {/* Out in the open, not in a MoreFilters drawer: it is the only
              filter here, and a drawer holding one control costs a press to
              reveal what the press was meant to save room for. */}
          <FwSelect aria-label="Filter by status" value={active} onChange={(e) => setActive(e.target.value)}>
            {ACTIVE_VALUES.map((v) => <option key={v || 'any'} value={v}>{ACTIVE_LABELS[v]}</option>)}
          </FwSelect>
          {filtersOn ? (
            <FwButton variant="ghost" className="or-toolbar__clear" onClick={clearFilters}>Clear filters</FwButton>
          ) : null}
        </Toolbar>
      )}
      <Split
        full={view.full}
        main={table}
        aside={view.openId ? (
          <PropertyDetail
            key={view.openId}
            id={view.openId}
            canUpdate={canUpdate}
            canDelete={canDelete}
            full={view.full}
            onToggleFull={view.toggleFull}
            onEdit={(p) => setSheet({ property: p })}
            onClose={view.close}
            onDeleted={view.close}
          />
        ) : null}
      />
      <PropertyForm
        open={!!sheet}
        property={sheet ? sheet.property : null}
        clients={clientOptions}
        clientsError={clientsQ.error}
        onClose={() => setSheet(null)}
        onSaved={() => setSheet(null)}
      />
    </div>
  );
}
